import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/brand";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkle,
  ArrowsClockwise,
  CaretDown,
  CaretUp,
  BookOpen,
  ArrowRight,
} from "@phosphor-icons/react";
import AIInsightFootnote from "@/components/AIInsightFootnote";

/**
 * "Your Forecast Story" card.
 *
 * Iter39 additions:
 *   • `dashboardMode` prop — renders ONLY the first paragraph with a
 *     truncation "…" and a "Read your full Forecast Story →" deep-link to
 *     /forecast#forecast-story. Trims the dashboard down to a teaser and
 *     kills the redundant "challenge of the week" quote block that used to
 *     sit underneath.
 *   • Cuppa-moment nudge — after 4+ regens in 10 minutes the backend flags
 *     `cuppa_nudge: true`; we surface it as a soft banner under the card.
 *   • Canonical `<AIInsightFootnote />` replaces the two ad-hoc footer
 *     paragraphs so the copy stays consistent with Goals and Variance cards.
 */
export default function ForecastStoryCard({ dashboardMode = false } = {}) {
  const [paragraphs, setParagraphs] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [plan, setPlan] = useState(null);
  const [canRegen, setCanRegen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Iter39 — soft nudge shown when the user has fired 4+ regens in 10 minutes.
  const [showCuppa, setShowCuppa] = useState(false);

  const load = async (regenerate = false) => {
    try {
      if (regenerate) setRegenerating(true);
      else setLoading(true);
      const res = await axios.get(
        `${API}/ai/forecast-story${regenerate ? "?regenerate=true" : ""}`,
      );
      setParagraphs(res.data.paragraphs || []);
      setGeneratedAt(res.data.generated_at || null);
      setPlan(res.data.plan || null);
      setCanRegen(!!res.data.can_regenerate);
      if (res.data.cuppa_nudge) setShowCuppa(true);
      if (regenerate) toast.success("Your forecast story has been refreshed.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Could not load your forecast story.";
      if (status === 402 || status === 429) {
        toast.message(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => {
    load(false);
  }, []);

  const onRegenerate = () => {
    if (!canRegen) {
      toast.message(
        "Your current story already reflects your latest numbers. Add a weekly pulse, log monthly actuals, or change your forecast to unlock a fresh take.",
      );
      return;
    }
    load(true);
  };

  const isOneOff = plan === "one_off" || plan === "cancelled" || plan === "none";

  if (loading) {
    return (
      <div
        className="card flex items-center gap-3 text-black/70"
        data-testid="forecast-story-loading"
      >
        <Sparkle size={20} weight="duotone" /> Reading your numbers…
      </div>
    );
  }

  if (!paragraphs.length) {
    return null;
  }

  const visible = dashboardMode ? paragraphs.slice(0, 1) : (expanded ? paragraphs : paragraphs.slice(0, 1));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="card scroll-mt-24"
      id="forecast-story"
      data-testid="forecast-story-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="section-eyebrow flex items-center gap-2">
            <BookOpen size={16} weight="duotone" /> Your forecast story
          </div>
          <h2 className="font-heading uppercase text-2xl mt-2">
            The year ahead, in plain English.
          </h2>
          {!dashboardMode && (
            <p className="text-sm text-black/70 mt-1">
              A short narrative of what your numbers are quietly telling you. Refreshes when you add
              new data — a weekly pulse, monthly actuals, or a change to your forecast.
            </p>
          )}
        </div>
        {!dashboardMode && (
          <button
            type="button"
            className={canRegen ? "btn-secondary" : "btn-secondary opacity-60"}
            onClick={onRegenerate}
            disabled={regenerating || !canRegen}
            data-testid="forecast-story-regenerate-btn"
            title={
              canRegen
                ? "New data since last generation — refresh your story"
                : "Your current story already reflects your latest numbers"
            }
          >
            <ArrowsClockwise
              size={14}
              weight="bold"
              className={regenerating ? "animate-spin" : ""}
            />
            {regenerating ? "Re-reading…" : "Regenerate"}
          </button>
        )}
      </div>

      {/* Iter39 — cuppa moment: soft, warm nudge after 4+ regens in 10 min.
          Never blocks, never scolds. Dismissable via the toggle. */}
      {showCuppa && !dashboardMode && (
        <div
          className="mt-4 p-3 rounded-xl border-[1.5px] border-black bg-[#FFF7E5] text-sm leading-relaxed flex items-start gap-3"
          data-testid="forecast-story-cuppa-nudge"
        >
          <span className="text-lg" aria-hidden>☕</span>
          <div className="flex-1">
            Cuppa moment — your Forecast Story's just refreshed a few times in the
            last few minutes. Nice, you're really engaging with your numbers. Worth
            pausing and giving the latest one a read before your next tweak, so it
            actually lands.
          </div>
          <button
            type="button"
            className="text-black/50 hover:text-black text-xs underline shrink-0"
            onClick={() => setShowCuppa(false)}
            data-testid="forecast-story-cuppa-dismiss"
          >
            Got it
          </button>
        </div>
      )}

      <div className="mt-5 space-y-5">
        {visible.map((p, i) => {
          const trimmed = (p || "").replace(/^\s+/, "");
          const match = trimmed.match(/^\*\*(.+?)\*\*\s*\n?([\s\S]*)$/);
          const subhead = match ? match[1].trim() : null;
          const body = match ? match[2].trim() : trimmed;
          const showSubhead = subhead && i > 0;
          // Iter39 dashboard mode: trail off with "…" on the last visible
          // paragraph so it's clear more story lives on the Forecast page.
          const isLastVisible = i === visible.length - 1;
          const bodyText = dashboardMode && isLastVisible
            ? body.replace(/[.!?]?\s*$/, "…")
            : body;
          return (
            <div
              key={i}
              className={i === 0 ? "border-l-[3px] border-[#FFBD4A] pl-4" : ""}
              data-testid={`forecast-story-para-${i}`}
            >
              {showSubhead && (
                <div className="font-heading uppercase text-lg mb-2">{subhead}</div>
              )}
              <p className="leading-relaxed text-black/85 whitespace-pre-line">{bodyText}</p>
            </div>
          );
        })}
      </div>

      {dashboardMode ? (
        <a
          href="/forecast#forecast-story"
          className="mt-5 btn-primary inline-flex"
          data-testid="forecast-story-read-full-btn"
        >
          Read your full Forecast Story <ArrowRight size={14} weight="bold" />
        </a>
      ) : (
        paragraphs.length > 1 && (
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold underline-offset-4 hover:underline"
            onClick={() => setExpanded((v) => !v)}
            data-testid="forecast-story-toggle-btn"
          >
            {expanded ? (
              <>
                Show less <CaretUp size={14} weight="bold" />
              </>
            ) : (
              <>
                Read the full story ({paragraphs.length} sections){" "}
                <CaretDown size={14} weight="bold" />
              </>
            )}
          </button>
        )
      )}

      {!dashboardMode && (
        <AIInsightFootnote generatedAt={generatedAt} className="mt-5" />
      )}
    </motion.div>
  );
}
